import { Theme, css } from '@emotion/react';
import React from 'react';

import { Recipient } from 'api/api';

import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { CaretRightIcon } from '~src/icons/caret-right';
import { fontStyles } from '~src/theme/fontStyles';
import { LinkButton } from '~src/ui/link-button';

import { applyPhoneNumberMask, formatRecipientToArray } from '~address-form/utils/format-ui';

interface AddressCardUIProps {
  handleClick: () => void;
  address: Recipient;
  boldenFirstLine: boolean;
  omitContactInfo?: boolean;
  disableKeyboard?: boolean;
}

export const AddressCardUI: React.VFC<AddressCardUIProps> = ({
  handleClick,
  address,
  boldenFirstLine,
  omitContactInfo = false,
  disableKeyboard,
}) => {
  const globalState = useGlobalState();

  const recipient = formatRecipientToArray(
    applyPhoneNumberMask(address),
    globalState.purchase_country,
    { omitContactInfo }
  );
  const { tICU } = useTranslations();

  return (
    <div css={styles.wrapper}>
      <div css={styles.body} data-test="user-info-box">
        {recipient.map((line, idx) => (
          <div
            key={idx}
            css={boldenFirstLine && idx === 0 ? styles.name : styles.line}
            data-test="user-details"
          >
            {line}
          </div>
        ))}
      </div>
      <LinkButton
        tabIndex={disableKeyboard ? -1 /* disable focus */ : undefined}
        css={styles.button}
        onClick={handleClick}
        data-test="open-arrow"
        aria-label={tICU('widget.aria.OPEN_ADDRESS_DETAILS')}
      >
        <CaretRightIcon />
      </LinkButton>
    </div>
  );
};

const styles = {
  wrapper: css({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  name: {
    fontVariationSettings: '"wght" 500',
  },
  line: (theme: Theme) =>
    css({
      marginTop: '2px',
      color: theme.darkGrey,
      textRendering: 'optimizeSpeed',
    }),
  body: {
    ...fontStyles.px14_20,
    width: '100%',
  },
  button: css({
    fontSize: 'inherit',
    fontVariationSettings: '"wght" 500',
    padding: '16px',
  }),
};
